import { useCallback } from 'react';
import { ApiResult } from 'services/api/result.type';
import useSWRInfinite, {
  SWRInfiniteConfiguration,
  SWRInfiniteResponse,
} from 'swr/infinite';
import { useInfiniteScroll } from './useInfiniteScroll';

type KeyFunc<D, T> = (
  pageIndex: number,
  previousPageData: D | null,
) => Readonly<T> | null;

type LoaderOptions<D> = SWRInfiniteConfiguration<D> & {
  infiniteScroll?: boolean;
};

export const useInfiniteLoader = <
  D extends { cursor?: string | null } | null,
  T extends unknown[]
>(
  keyFunc: KeyFunc<D, T>,
  fetchFunc: (...args: T) => ApiResult<D> | Promise<D> | D,
  options?: LoaderOptions<D>,
): SWRInfiniteResponse<D> => {
  const { infiniteScroll = true, ...swrOptions } = options ?? {};
  const fetcher = useCallback(fetchFunc, []);

  const rData = useSWRInfinite<D>((pageIndex, previousPageData) => {
    if (pageIndex > 0 && !previousPageData?.cursor) return null;

    const args = keyFunc(pageIndex, previousPageData);
    if (args === null) return null;

    return [fetcher, ...args];
  }, swrOptions);

  useInfiniteScroll(() => {
    if (!rData.isValidating && rData.data?.length === rData.size)
      void rData.setSize(rData.size + 1);
  }, 100, infiniteScroll);

  return rData;
};
